import { Injectable, Inject } from '@angular/core';
import { HttpResponse, HttpClient } from '@angular/common/http';
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  map,
  distinctUntilChanged,
  filter,
  mergeMap
} from 'rxjs/operators';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import {
  ReusableStreamProperty,
  MuniEnvironmentParams
} from '@ipreo/muni-shared';
import { GameDayRootReducerState } from '../../state/root.state';
import { SetActiveAccount } from '../../state/profile/actions';
import { PortfolioOverviewData } from '../models/profile';
import { getFirmId } from '../components/portfolio/portfolio.calculations';

@Injectable()
export class AccountService {
  @ReusableStreamProperty
  public readonly activeAccount: Observable<string> = this.store.pipe(
    select(
      ({
        gameDay: {
          profile: { activeAccount }
        }
      }: GameDayRootReducerState) => activeAccount
    ),
    distinctUntilChanged()
  );

  private apiHost: string;

  constructor(
    private http: HttpClient,
    private store: Store<GameDayRootReducerState>,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.apiHost = (config.params as MuniEnvironmentParams).apiHost;
  }

  public resetVisibility(): void {
    this.store.dispatch(new SetActiveAccount());
  }

  public getAccount(): Observable<PortfolioOverviewData> {
    const firmId: Observable<number> = this.store.pipe(
      map(
        ({
          gameDay: {
            profile: { activeAccount, loading },
            data: {
              // rawData: { orders }
              orders
            }
          }
        }) => ({
          activeAccount,
          orders,
          loading
        })
      ),
      filter(({ orders, activeAccount }) => !!orders && !!activeAccount),
      distinctUntilChanged(
        (x, y) => x.activeAccount === y.activeAccount && x.loading === y.loading
      ),
      filter(({ loading }) => !!loading),
      map(({ activeAccount, orders }) => getFirmId(orders, activeAccount))
    );

    return firmId.pipe(
      mergeMap(
        (id: number): Observable<PortfolioOverviewData> => {
          if (!id) {
            return of(new PortfolioOverviewData());
          }

          return this.loadAccount(id);
        }
      )
    );
  }

  private loadAccount(firmId: number): Observable<PortfolioOverviewData> {
    return this.http
      .get(`${this.apiHost}/api/v1/accounts/${firmId}`, {
        observe: 'response'
      })
      .pipe(
        map((response: HttpResponse<PortfolioOverviewData>) => {
          const data: PortfolioOverviewData =
            response.status < 400 && response.body;

          if (!data) {
            throw new Error('No portfolio data');
          }

          return data;
        }),
        catchError((error: Error) => of(new PortfolioOverviewData()))
      );
  }
}
